<script setup>
import { ref, watch, computed } from 'vue';
import Dialog from 'primevue/dialog';
import Button from 'primevue/button';
import Select from 'primevue/select';
import RadioButton from 'primevue/radiobutton';
import InputText from 'primevue/inputtext';
import DatePicker from 'primevue/datepicker';
import { useToast } from 'primevue/usetoast';
import { useWorksStore } from '@/stores/work.js';
import workApi from '@/api/workApi.js';

const props = defineProps({
  visible: { type: Boolean, default: false },
  contractId: { type: [Number, String], required: false, default: null },
  workId: { type: [Number, String], required: false, default: null },
  contractTitle: { type: String, required: false, default: '' },
});
const emit = defineEmits(['update:visible', 'close', 'saved']);

const toast = useToast();
const worksStore = useWorksStore();

// Поля формы
const mode = ref('type'); // 'type' | 'manual'
const workTypes = ref([]); // [{label, value, raw}]
const selectedTypeId = ref(null);
const customName = ref('');
const termStart = ref(null); // Date | null
const termEnd = ref(null);   // Date | null

const loading = ref(false);

const selectedType = computed(() => {
  const id = selectedTypeId.value;
  if (!id) return null;
  const opt = workTypes.value.find(o => o.value === id);
  return opt ? opt.raw : null;
});

const showCustomName = computed(() => {
  if (mode.value === 'manual') return true;
  return !!selectedType.value?.is_other;
});

const formValid = computed(() => {
  if (mode.value === 'manual') return customName.value.trim().length > 0;
  if (!selectedTypeId.value) return false;
  if (selectedType.value?.is_other) {
    return customName.value.trim().length > 0;
  }
  return true;
});

// Когда открылась форма — загрузить типы и (при редактировании) данные
watch(
    () => props.visible,
    async (vis) => {
      if (!vis) return;
      await initForm();
    },
    { immediate: false }
);

async function initForm() {
  loading.value = true;
  try {
    // Типы работ
    const types = await worksStore.loadWorkTypes({ is_active: true });
    workTypes.value = (Array.isArray(types) ? types : []).map(t => ({
      label: `${t.item_name}`,
      value: t.id,
      raw: t,
    }));


    // Сброс значений
    if (!props.workId) {
      mode.value = 'type';
      selectedTypeId.value = null;
      customName.value = '';
      termStart.value = null;
      termEnd.value = null;
    }

    // Если редактирование — подгрузим поля
    if (props.workId) {
      const data = await workApi.getWork(Number(props.workId));
      // Если есть work_type -> режим type, иначе manual
      if (data?.work_type) {
        mode.value = 'type';
        selectedTypeId.value = data.work_type;
        customName.value = data?.custom_name || '';
      } else {
        mode.value = 'manual';
        customName.value = data?.name || data?.custom_name || '';
      }
      termStart.value = data?.term_start ? new Date(data.term_start) : null;
      termEnd.value = data?.term_end ? new Date(data.term_end) : null;
    }
  } catch (e) {
    console.error('Ошибка инициализации формы работы', e);
    toast.add({ severity: 'error', summary: 'Ошибка', detail: 'Не удалось подготовить форму', life: 4000 });
  } finally {
    loading.value = false;
  }
}

function toIsoOrUndefined(d) {
  if (!d || isNaN(new Date(d).getTime())) return undefined;
  const dt = new Date(
      d.getFullYear(),
      d.getMonth(),
      d.getDate(),
      0, 0, 0, 0
  );
  return dt.toISOString();
}

async function submit() {
  if (!formValid.value) {
    toast.add({ severity: 'warn', summary: 'Заполните форму', detail: 'Проверьте обязательные поля', life: 2500 });
    return;
  }
  const payload = {};
  const ts = toIsoOrUndefined(termStart.value);
  const te = toIsoOrUndefined(termEnd.value);
  if (ts) payload.term_start = ts;
  if (te) payload.term_end = te;

  if (mode.value === 'manual') {
    payload.custom_name = customName.value.trim();
  } else {
    payload.work_type = selectedTypeId.value;
    if (selectedType.value?.is_other && customName.value.trim()) {
      payload.custom_name = customName.value.trim();
    }
  }

  try {
    let result;
    if (props.workId) {
      result = await worksStore.updateWork(Number(props.workId), payload);
      toast.add({ severity: 'success', summary: 'Сохранено', detail: 'Работа обновлена', life: 2200 });
      emit('saved', result);
    } else {
      result = await worksStore.createWork({
        contract: Number(props.contractId),
        ...payload,
      });
      toast.add({ severity: 'success', summary: 'Создано', detail: 'Работа создана', life: 2200 });
      emit('saved', result);
    }
    close();
  } catch (e) {
    console.error('Ошибка сохранения работы', e);
    const detail = e?.response?.data?.detail || 'Не удалось сохранить работу';
    toast.add({ severity: 'error', summary: 'Ошибка', detail, life: 4000 });
  }
}

function close() {
  emit('update:visible', false);
  emit('close');
}

// Автоочистка customName при выборе не-«Другое»
watch(selectedTypeId, () => {
  if (mode.value === 'type' && selectedType.value && !selectedType.value.is_other) {
    customName.value = '';
  }
});

const dialogHeader = computed(() =>
    props.workId
        ? `Редактировать работу в Договорe №${props.contractTitle || '—'}`
        : `Добавить работу в Договор №${props.contractTitle || '—'}`
);

</script>

<template>
  <Dialog
      :visible="visible"
      @update:visible="(v)=> v ? null : close()"
      modal
      :header="dialogHeader"
      :style="{ width: '680px', maxWidth: '95vw' }"
      :closable="true"
      :position="'top'"
  >
    <form class="flex flex-col gap-5" @submit.prevent="submit">
      <!-- Режим ввода названия -->
      <div class="flex items-center gap-5">
        <div class="flex items-center gap-2">
          <RadioButton inputId="modeType" name="mode" value="type" v-model="mode" />
          <label for="modeType">Выбрать тип</label>
        </div>
        <div class="flex items-center gap-2">
          <RadioButton inputId="modeManual" name="mode" value="manual" v-model="mode" />
          <label for="modeManual">Указать вручную</label>
        </div>
      </div>

      <!-- Тип работ -->
      <div v-if="mode === 'type'" class="flex flex-col gap-2">
        <label for="workType" class="text-sm">Тип работ</label>
        <Select
            inputId="workType"
            v-model="selectedTypeId"
            :options="workTypes"
            optionLabel="label"
            optionValue="value"
            class="w-full"
            placeholder="Выберите тип"
            :disabled="loading"
        />
      </div>

      <!-- Название вручную (manual или is_other) -->
      <div v-if="showCustomName" class="flex flex-col gap-2">
        <label for="customName" class="text-sm">Название</label>
        <InputText
            id="customName"
            v-model.trim="customName"
            placeholder="Например: Монтаж пожарной сигнализации"
            class="w-full"
        />
      </div>

      <!-- Даты -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div class="flex flex-col gap-2">
          <label for="termStart" class="text-sm">Начало работ</label>
          <DatePicker
              inputId="termStart"
              v-model="termStart"
              dateFormat="yy-mm-dd"
              :manualInput="true"
              class="w-full"
          />
        </div>
        <div class="flex flex-col gap-2">
          <label for="termEnd" class="text-sm">Окончание работ</label>
          <DatePicker
              inputId="termEnd"
              v-model="termEnd"
              dateFormat="yy-mm-dd"
              :manualInput="true"
              class="w-full"
          />
        </div>
      </div>

      <div class="flex justify-center gap-3 pt-2">
        <Button :label="props.workId ? 'Сохранить' : 'Создать'" type="submit" severity="info" rounded :disabled="!formValid" />
        <Button label="Отмена" type="button" severity="secondary" rounded @click="close" />
      </div>
    </form>
  </Dialog>
</template>

<style scoped>
</style>